// Small, allocation-free math helpers for the Corridor Zero raycaster engine.
// Kept dependency-free and deterministic so gameplay simulation is reproducible
// under the fixed 1/60 timestep (important for the deterministic round-end test).

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Wrap an angle into [0, TAU). */
export function normalizeAngle(angle: number): number {
  let a = angle % TAU;
  if (a < 0) a += TAU;
  return a;
}

/** Smallest signed delta to turn from `from` toward `to` (range -PI..PI). */
export function angleDelta(from: number, to: number): number {
  let d = (to - from) % TAU;
  if (d < -Math.PI) d += TAU;
  if (d > Math.PI) d -= TAU;
  return d;
}

/**
 * Pack RGBA into a 32-bit integer matching the in-memory byte order of a
 * Uint8ClampedArray-backed ImageData on little-endian platforms (R,G,B,A).
 * All target browsers are little-endian; the raycaster writes its framebuffer
 * through a Uint32 view over the same ArrayBuffer as the ImageData for speed.
 */
export function packRGBA(r: number, g: number, b: number, a = 255): number {
  return ((a << 24) | (b << 16) | (g << 8) | r) >>> 0;
}

export function rUnpack(px: number): number {
  return px & 0xff;
}
export function gUnpack(px: number): number {
  return (px >> 8) & 0xff;
}
export function bUnpack(px: number): number {
  return (px >> 16) & 0xff;
}
export function aUnpack(px: number): number {
  return (px >> 24) & 0xff;
}

/** Multiply a packed pixel's RGB by a scalar (0..1) for distance shading. Alpha preserved. */
export function shadePixel(px: number, scale: number): number {
  const a = px & 0xff000000;
  const r = Math.min(255, ((px & 0xff) * scale) | 0);
  const g = Math.min(255, (((px >> 8) & 0xff) * scale) | 0);
  const b = Math.min(255, (((px >> 16) & 0xff) * scale) | 0);
  return (a | (b << 16) | (g << 8) | r) >>> 0;
}

/** Deterministic small PRNG (mulberry32) for reproducible procedural textures. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
